import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import PrintIcon from "@mui/icons-material/Print";
import AddItemDialog from "./OrderAddEditeItemToOrder/AddItemDialog";
import EditeItemsDialog from "./OrderAddEditeItemToOrder/EditeItemsDialog";
import DeleteItemsDialog from "./OrderAddEditeItemToOrder/DeleteItemsDialog";
import CloseItemTimePrepration from "./OrderAddEditeItemToOrder/CloseItemTimePrepration";
import EditeOrderCustomer from "./OrderActions/EditeOrderCustomer";
import EditeOrderDataDilog from "./OrderActions/EditeOrderDataDilog";
import DeleteOrderDialog from "./OrderActions/DeleteOrderDialog";
import OrderInvoicePayment from "./OrderInvoice/OrderInvoicePayment";
import OrderDetailsSection from "./OrderData/OrderDetailsSection";
import OrderItemsSection from "./OrderData/OrderItemsSection";
import OrderCustomerUserInfoSection from "./OrderData/OrderCustomerUserInfoSection";
import { getOrderDetails } from "../../../../services/Admin/OrdersApi";
import { handleBrowserPrintBeforePay } from "./PrintOrder/PrintBefore/printUtilsBeforePay";
import {
  normalizeOrderForPrint,
  toSafeNumber, 
} from "./PrintOrder/printNormalizer";
import { useAuth } from "../../../../Context/AuthContext";

const CASHIER_DELETE_ORDER_LIMIT = 199;
const CASHIER_DELETE_ITEM_LIMIT = 99;

const OrderInfo = ({ order, onRefresh, onInvoicePaymentSuccess }) => {
  const [openAddItem, setOpenAddItem] = useState(false);
  const [openEditItem, setOpenEditItem] = useState(false);
  const [openDeleteItem, setOpenDeleteItem] = useState(false);
  const [openCloseTime, setOpenCloseTime] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openEditCustomer, setOpenEditCustomer] = useState(false);
  const [openEditOrder, setOpenEditOrder] = useState(false);
  const [openDeleteOrder, setOpenDeleteOrder] = useState(false);
  const [totalItemsValue, setTotalItemsValue] = useState(0);
  const [liveInvoiceData, setLiveInvoiceData] = useState(null);
  const [productsMap, setProductsMap] = useState({});
const { hasPermission, canManageOrders, userRole, userType } = useAuth();

const [localOrder, setLocalOrder] = useState(order || null);

// Helper: check if print is allowed for this order
// يجب تعريف الدالة داخل useMemo أو useCallback بعد التأكد من وجود localOrder
const isPrintAllowed = React.useCallback(() => {
  if (!localOrder) return false;
  if (canManageOrders()) return true;
  if (!localOrder.id) return false;
  const printed = localStorage.getItem(`order_printed_${localOrder.id}`);
  return !printed;
}, [localOrder, canManageOrders]);

// يجب عدم استدعاء isPrintAllowed إلا بعد التأكد من وجود localOrder
const canPrint = React.useMemo(() => {
  if (!localOrder) return false;
  return isPrintAllowed() && (canManageOrders() || hasPermission("print"));
}, [localOrder, isPrintAllowed, canManageOrders, hasPermission]);

const orderSubTotalBeforeFees = React.useMemo(() => {
  const orderItems = Array.isArray(localOrder?.order_items) ? localOrder.order_items : [];
  const itemsTotal = orderItems.reduce((sum, item) => sum + toSafeNumber(item?.total), 0);

  return (
    itemsTotal ||
    toSafeNumber(totalItemsValue) ||
    toSafeNumber(localOrder?.sub_total) ||
    toSafeNumber(localOrder?.total)
  );
}, [localOrder, totalItemsValue]);

const isCashierAdmin = userType === "admin" && userRole === "cashier";
const isManagerAdmin = userType === "admin" && userRole === "manager";
const canDeleteOrder = !isManagerAdmin && (canManageOrders() || (
  isCashierAdmin && orderSubTotalBeforeFees <= CASHIER_DELETE_ORDER_LIMIT
));

const deleteBlockedMessage = isManagerAdmin
  ? "ليس لديك صلاحية حذف هذا الطلب."
  : `الكاشير يستطيع حذف الطلبات التي لا تتجاوز ${CASHIER_DELETE_ORDER_LIMIT} جنيه قبل الخصم والضريبة فقط.`;

const selectedItemTotalValue = React.useMemo(() => {
  const total = toSafeNumber(selectedItem?.total);
  if (total) return total;

  return toSafeNumber(selectedItem?.price) * toSafeNumber(selectedItem?.quantity);
}, [selectedItem]);

const selectedItemUnitValue = React.useMemo(() => {
  const price = toSafeNumber(selectedItem?.price);
  if (price) return price;

  const quantity = toSafeNumber(selectedItem?.quantity);
  return quantity ? selectedItemTotalValue / quantity : 0;
}, [selectedItem, selectedItemTotalValue]);

const canDeleteSelectedItem = selectedItem ? (
  isCashierAdmin
    ? selectedItemUnitValue <= CASHIER_DELETE_ITEM_LIMIT
    : hasPermission("delete")
) : false;

const deleteItemBlockedMessage = `الكاشير يستطيع حذف وحدة من الأصناف التي لا يتجاوز سعرها ${CASHIER_DELETE_ITEM_LIMIT} جنيه فقط.`;

  // ✅ المصدر الأساسي للعرض داخل الديالوج

  const orderId = order?.id || order?.order_id;

  const refreshOrderDetails = useCallback(async () => {
    if (!orderId) {
      setLocalOrder(order || null);
      return;
    }

    try {
      const response = await getOrderDetails(orderId);
      const freshOrder =
        response?.data?.order ||
        response?.order ||
        response?.data ||
        response ||
        null;

      if (freshOrder && typeof freshOrder === "object") {
        setLocalOrder(freshOrder);
      } else {
        setLocalOrder(order || null);
      }
    } catch (error) {
      console.error("Failed to fetch order details", error);
      setLocalOrder(order || null);
    }
  }, [orderId, order]);

  useEffect(() => {
    refreshOrderDetails();
  }, [refreshOrderDetails]);

  if (!localOrder) return null;

  const handleItemAdded = (data) => {
    refreshOrderDetails();
    onRefresh?.();
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setOpenEditItem(true);
  };

  const handleItemUpdated = (data) => {
    refreshOrderDetails();
    onRefresh?.();
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setOpenDeleteItem(true);
  };

  const handleItemDeleted = (data) => {
    refreshOrderDetails();
    onRefresh?.();
  };

  const handleCloseTime = (item) => {
    setSelectedItem(item);
    setOpenCloseTime(true);
  };

  const handleTimeClosed = (data) => {
    refreshOrderDetails();
    onRefresh?.();
  };

  const handlePreparationItemStatusUpdated = (itemId, status) => {
    setLocalOrder((prev) => {
      if (!prev || !Array.isArray(prev.order_items)) return prev;

      return {
        ...prev,
        order_items: prev.order_items.map((item) =>
          String(item?.id) === String(itemId) ? { ...item, status } : item
        ),
      };
    });
  };

  // ✅ هنا التعديل المهم: عدّل order.customer نفسها لأنها مصدر العرض
  const handleCustomerUpdated = (payload) => {

    // updateOrderCustomer بيرجع: { data: {...customer}, message: "..." }
    const customer = payload?.data || payload?.customer || null;

    if (customer) {
      setLocalOrder((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          // ✅ تحديث customer object
          customer: {
            ...(prev.customer || {}),
            ...customer,
          },

          // (اختياري) لو في أماكن تانية بتعرض customer_name/customer_phone
          customer_name: customer.name ?? prev.customer_name,
          customer_phone: customer.phone ?? prev.customer_phone,
          customer_address: customer.address ?? prev.customer_address,
          customer_address2: customer.address2 ?? prev.customer_address2,
          customer_address3: customer.address3 ?? prev.customer_address3,
          customer_phone2: customer.phone2 ?? prev.customer_phone2,
          customer_phone3: customer.phone3 ?? prev.customer_phone3,
        };
      });
    }

    refreshOrderDetails();
    onRefresh?.();
  };

  const handleOrderUpdated = (data) => {
    refreshOrderDetails();
    onRefresh?.();
  };

  const handleOrderDeleted = (data) => {
    onRefresh?.();
  };

  const handleInvoiceCreated = (data) => {
    // إزالة علامة الطباعة من localStorage بعد حفظ الفاتورة
    if (localOrder?.id) {
      localStorage.removeItem(`order_printed_${localOrder.id}`);
    }
    onRefresh?.();
    onInvoicePaymentSuccess?.(); // إغلاق الديالوج عند نجاح الفاتورة
  };

  const getAmountFromSaved = (input, mode, subTotal) => {
    const parsed = toSafeNumber(input);
    if (mode === "ratio") {
      return (subTotal * parsed) / 100;
    }
    return parsed;
  };

  const handlePrint = () => {
    if (!localOrder?.id) {
      alert("لا يوجد طلب للطباعة");
      return;
    }

    // منع الطباعة إذا لم يكن مسموحًا
    if (!isPrintAllowed()) {
      alert("تمت طباعة هذا الطلب بالفعل. لا يمكنك الطباعة مرة أخرى.");
      return;
    }

    const getCurrentUserName = () => {
      try {
        const rawUser = localStorage.getItem("user");
        if (!rawUser) return "";
        const parsedUser = JSON.parse(rawUser);
        return (
          parsedUser?.name ||
          parsedUser?.user_name ||
          parsedUser?.username ||
          ""
        );
      } catch (e) {
        return "";
      }
    };

    const captainOrderName =
      localOrder?.captain_order?.name ||
      localOrder?.captain?.name ||
      localOrder?.captain_order_name ||
      (localOrder?.captain_order_id ? `#${localOrder.captain_order_id}` : "");

    const invoiceCreatedByName =
      localOrder?.invoice_created_by_name ||
      localOrder?.created_by_name ||
      localOrder?.created_by ||
      localOrder?.user?.name ||
      localOrder?.user_name ||
      getCurrentUserName();

    // Order normalization يضمن customer/items/order_items/product_name بشكل موحد
    const orderForPrint = {
      ...normalizeOrderForPrint(localOrder, totalItemsValue, productsMap),
      captain_order_name: captainOrderName,
      invoice_created_by_name: invoiceCreatedByName,
    };

    const addPrintMeta = (invoiceData = {}) => ({
      ...invoiceData,
      captain_order_name: captainOrderName,
      invoice_created_by_name: invoiceCreatedByName,
    });

    // 1) لو عندك liveInvoiceData
    if (liveInvoiceData) {
      handleBrowserPrintBeforePay(
        orderForPrint,
        addPrintMeta(liveInvoiceData),
        localOrder.id,
        canManageOrders // مررها كدالة
      );
      return;
    }

    // 2) fallback localStorage
    const subTotal = toSafeNumber(totalItemsValue || orderForPrint.total || 0);
    const savedRaw = localStorage.getItem(
      `invoiceCalculations_${localOrder.id}`,
    );

    if (savedRaw) {
      try {
        const parsed = JSON.parse(savedRaw);
        const service = getAmountFromSaved(
          parsed.service_input,
          parsed.service_mode,
          subTotal,
        );
        const delivery_service = getAmountFromSaved(
          parsed.delivery_service_input,
          parsed.delivery_service_mode,
          subTotal,
        );
        const tax = getAmountFromSaved(
          parsed.tax_input,
          parsed.tax_mode,
          subTotal,
        );
        const discount = getAmountFromSaved(
          parsed.discount_input,
          parsed.discount_mode,
          subTotal,
        );

        const invoiceData = {
          service,
          service_ratio:
            parsed.service_mode === "ratio"
              ? toSafeNumber(parsed.service_input)
              : 0,
          service_is_percentage: parsed.service_mode === "ratio" ? 1 : 0,

          delivery_service,
          delivery_service_ratio:
            parsed.delivery_service_mode === "ratio"
              ? toSafeNumber(parsed.delivery_service_input)
              : 0,
          delivery_service_is_percentage:
            parsed.delivery_service_mode === "ratio" ? 1 : 0,

          tax,
          tax_ratio:
            parsed.tax_mode === "ratio" ? toSafeNumber(parsed.tax_input) : 0,
          tax_is_percentage: parsed.tax_mode === "ratio" ? 1 : 0,

          discount,
          discount_ratio:
            parsed.discount_mode === "ratio"
              ? toSafeNumber(parsed.discount_input)
              : 0,
          discount_is_percentage: parsed.discount_mode === "ratio" ? 1 : 0,

          sub_total: subTotal,
          total_amount:
            toSafeNumber(parsed.total_amount) ||
            subTotal + service + delivery_service + tax - discount,
        };

        handleBrowserPrintBeforePay(
          orderForPrint,
          addPrintMeta(invoiceData),
          localOrder.id,
          canManageOrders // مررها كدالة
        );
        return;
      } catch (e) {
        console.warn("Failed to parse invoice calculations", e);
      }
    }

    // 3) fallback أخير
    handleBrowserPrintBeforePay(
      orderForPrint,
      addPrintMeta({
        service: 0,
        service_ratio: 0,
        service_is_percentage: 0,
        delivery_service: 0,
        delivery_service_ratio: 0,
        delivery_service_is_percentage: 0,
        tax: 0,
        tax_ratio: 0,
        tax_is_percentage: 0,
        discount: 0,
        discount_ratio: 0,
        discount_is_percentage: 0,
        sub_total: subTotal,
        total_amount: subTotal,
      }),
      localOrder.id,
      canManageOrders // مررها كدالة
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <OrderDetailsSection
            order={localOrder}
            onEditOrder={() => setOpenEditOrder(true)}
            onDeleteOrder={() => {
              if (canDeleteOrder) setOpenDeleteOrder(true);
            }}
            canDeleteOrder={canDeleteOrder}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <OrderCustomerUserInfoSection
            order={localOrder}
            onEditCustomer={() => setOpenEditCustomer(true)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <OrderItemsSection
        order={localOrder}
        onAddItem={() => setOpenAddItem(true)}
        onEditItem={handleEditItem}
        onDeleteItem={handleDeleteItem}
        onCloseTime={handleCloseTime}
        onTotalItemsValueChange={setTotalItemsValue}
        onProductsMapReady={setProductsMap} // ✅ جديد
        onPreparationItemStatusUpdated={handlePreparationItemStatusUpdated}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
{canPrint && (
  <Button
    variant="contained"
    color="primary"
    startIcon={<PrintIcon />}
    onClick={handlePrint}
    sx={{ fontWeight: "bold" }}
  >
    طباعة قبل الدفع
  </Button>
)}
      </Box>

      <OrderInvoicePayment
        order={localOrder}
        totalItemsValue={totalItemsValue}
        onInvoiceCreated={handleInvoiceCreated}
        onCalculationsChange={setLiveInvoiceData}
        productsMap={productsMap} // ✅ ده المهم
      />

      <AddItemDialog
        open={openAddItem}
        onClose={() => setOpenAddItem(false)}
        orderId={localOrder.id}
        onAdded={handleItemAdded}
      />

      <EditeItemsDialog
        open={openEditItem}
        onClose={() => setOpenEditItem(false)}
        item={selectedItem}
        onUpdated={handleItemUpdated}
      />

      <DeleteItemsDialog
        open={openDeleteItem}
        onClose={() => setOpenDeleteItem(false)}
        item={selectedItem}
        canDeleteItem={canDeleteSelectedItem}
        blockedMessage={deleteItemBlockedMessage}
        itemTotalValue={selectedItemTotalValue}
        itemUnitValue={selectedItemUnitValue}
        onDeleted={handleItemDeleted}
      />

      <CloseItemTimePrepration
        open={openCloseTime}
        onClose={() => setOpenCloseTime(false)}
        item={selectedItem}
        onClosed={handleTimeClosed}
      />

      <EditeOrderCustomer
        open={openEditCustomer}
        onClose={() => setOpenEditCustomer(false)}
        order={localOrder}
        onUpdated={handleCustomerUpdated}
      />

      <EditeOrderDataDilog
        open={openEditOrder}
        onClose={() => setOpenEditOrder(false)}
        order={localOrder}
        onUpdated={handleOrderUpdated}
      />

      <DeleteOrderDialog
        open={openDeleteOrder}
        onClose={() => setOpenDeleteOrder(false)}
        order={localOrder}
        canDeleteOrder={canDeleteOrder}
        blockedMessage={deleteBlockedMessage}
        orderSubTotalBeforeFees={orderSubTotalBeforeFees}
        onDeleted={handleOrderDeleted}
      />
    </Paper>
  );
};

export default OrderInfo;
